import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import { SI_DISPLAY_NAME } from '../siBrand';
import CoopLeaderboard from './CoopLeaderboard';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { isEditableTarget } from '../manualPlay';
import { SI_CANVAS_H, SI_CANVAS_STYLE, SI_CANVAS_W, SI_PLAYFIELD_SX, drawSpaceInvaderScene } from '../visuals/SpaceInvaderScene';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import SiLevelBanner, { SI_MAX_LEVEL, formatSiLevel } from '../visuals/SiLevelBanner';
import { ARCADE } from '../../../utils/config';
import { COLOR, GAME_ACCENT } from '../ui/tokens';
import { createSnapshotBuffer, pushSnapshot, clearRoomParam, inviteRoomStatus, roomErrorMessage } from './onlineRoomsNet';
import {
    SHIP_COLOR, SHIP_LABEL, SI_FIRE_REPEAT_MS, SI_INTERP_DELAY_MS, SiRoomSocket, beaconLeaveSi,
    clearSiSeat, loadSiSeat, saveSiSeat, sampleSiSnapshot, sceneShips, siHeldMove, siInviteLink,
    siRoomIdFromSearch, siRooms,
} from './siCoopNet';

const GAME_ID = 'space_invader';
const ACCENT = GAME_ACCENT.space_invader;
const UPLOAD_URL = ARCADE.space_invader?.UPLOAD_URL;

const PANEL_SX = {
    p: 2,
    borderRadius: 2,
    border: '1px solid rgba(0,255,0,0.3)',
    bgcolor: 'rgba(0, 12, 0, 0.85)',
    width: '100%',
    maxWidth: 640,
    alignSelf: 'center',
};

const END_TEXT = {
    victory: 'Victory — all 5 levels cleared',
    no_lives: 'Out of lives',
    invaded: 'The swarm landed',
    step_limit: 'Time is up',
    forfeit: 'Your partner left',
    no_show: 'Your partner never connected',
    cancelled: 'Room closed',
    server_error: 'Server error — match stopped',
};

export function siRoomIdFromLocation() {
    if (typeof window === 'undefined') return null;
    return siRoomIdFromSearch(window.location.search);
}

/**
 * Space Invader Online Co-op: two ships on two computers against one fleet.
 * The gateway ticks the match (shared waves, lives and team score); this page
 * sends only its own ship's input and draws interpolated server snapshots.
 */
export default function SpaceInvaderCoop({ headerContent, onLiveChange }) {
    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const [grant, setGrant] = useState(null);
    /** idle | waiting | ready | running | paused | finished */
    const [phase, setPhase] = useState('idle');
    const [conn, setConn] = useState('closed');
    const [message, setMessage] = useState('');
    const [result, setResult] = useState(null);
    const [seats, setSeats] = useState({});
    const [waitingRooms, setWaitingRooms] = useState([]);
    const [code, setCode] = useState(() => siRoomIdFromLocation() || '');
    const [busy, setBusy] = useState(false);
    const [hud, setHud] = useState({
        team_score: 0, lives: 4, level: 1, max_level: SI_MAX_LEVEL, transition: 0, won: false, ships: {},
    });

    const canvasRef = useRef(null);
    const endedRef = useRef(false);
    const sockRef = useRef(null);
    const bufRef = useRef(createSnapshotBuffer());
    const keysRef = useRef(new Set());
    const lastSentRef = useRef('stay');
    const fireTimerRef = useRef(null);
    const grantRef = useRef(null);
    const phaseRef = useRef(phase);
    phaseRef.current = phase;

    const live = phase === 'waiting' || phase === 'ready' || phase === 'running' || phase === 'paused';
    useEffect(() => { if (onLiveChange) onLiveChange(live); }, [live, onLiveChange]);

    const takeHud = (m) => {
        if (typeof m.team_score === 'number') {
            setHud((h) => ({
                team_score: m.team_score,
                lives: typeof m.lives === 'number' ? m.lives : h.lives,
                level: typeof m.level === 'number' ? m.level : h.level,
                max_level: typeof m.max_level === 'number' ? m.max_level : h.max_level,
                transition: typeof m.transition === 'number' ? m.transition : 0,
                won: typeof m.won === 'boolean' ? m.won : h.won,
                ships: m.ships || h.ships,
            }));
        }
    };

    const onMessage = useCallback((msg) => {
        switch (msg.type) {
            case 'welcome':
            case 'peer':
            case 'start':
                if (msg.phase) setPhase(msg.phase);
                if (msg.seats) setSeats(msg.seats);
                takeHud(msg);
                if (msg.type === 'start') setMessage('Defend together!');
                if (msg.type === 'welcome' && msg.snapshot) {
                    pushSnapshot(bufRef.current, { ...msg.snapshot, tick: msg.tick }, performance.now());
                    takeHud(msg.snapshot);
                }
                break;
            case 'state':
                pushSnapshot(bufRef.current, msg, performance.now());
                if (msg.phase && msg.phase !== phaseRef.current) setPhase(msg.phase);
                takeHud(msg);
                break;
            case 'end':
                endedRef.current = true;
                pushSnapshot(bufRef.current, msg, performance.now());
                takeHud(msg);
                setPhase('finished');
                setResult({ reason: msg.reason, team_score: msg.team_score, level: msg.level });
                clearSiSeat();
                break;
            case 'kicked':
                setMessage('This ship was opened in another tab.');
                break;
            case 'error':
                if (endedRef.current && msg.code === 'room_finished') break; // result already shown (incl. replayed end)
                if (msg.code === 'room_finished' || msg.code === 'room_not_found' || msg.code === 'bad_seat_token') {
                    setMessage('That room is no longer available.');
                    clearSiSeat();
                    setPhase('idle');
                    setGrant(null);
                }
                break;
            default:
                break;
        }
    }, []);

    const openSocket = useCallback((g) => {
        if (sockRef.current) sockRef.current.close();
        bufRef.current = createSnapshotBuffer();
        lastSentRef.current = 'stay';
        grantRef.current = g;
        setGrant(g);
        setResult(null);
        setPhase(g.phase || 'waiting');
        setSeats(g.seats || {});
        saveSiSeat(g);
        const s = new SiRoomSocket(g, {
            onMessage,
            onStatus: (st) => setConn(st),
            graceMs: (g.reconnect_grace_s || 20) * 1000,
        });
        sockRef.current = s;
        s.connect();
    }, [onMessage]);

    const run = useCallback(async (fn) => {
        setBusy(true);
        setMessage('');
        try {
            const g = await fn();
            endedRef.current = false;
            openSocket(g);
        } catch (e) {
            setMessage(roomErrorMessage(e));
            clearRoomParam('si_room');
        } finally {
            setBusy(false);
        }
    }, [openSocket]);

    const createRoom = () => run(() => siRooms.create({}));
    const quickMatch = () => run(() => siRooms.match({}));
    const joinRoom = (id) => run(() => siRooms.join(String(id || code).trim()));

    const leave = useCallback(() => {
        const g = grantRef.current;
        if (g) beaconLeaveSi(g.room_id, g.seat_token);
        if (sockRef.current) sockRef.current.close();
        sockRef.current = null;
        grantRef.current = null;
        clearSiSeat();
        setGrant(null);
        setPhase('idle');
        setConn('closed');
    }, []);

    // Resume a seat after a reload (same tab, inside the grace window).
    useEffect(() => {
        const saved = loadSiSeat();
        if (saved && saved.room_id && saved.seat_token) {
            siRooms.info(saved.room_id)
                .then((info) => {
                    if (info && info.phase !== 'finished') openSocket({ ...info, ...saved });
                    else clearSiSeat();
                })
                .catch(() => clearSiSeat());
        }
        return () => { if (sockRef.current) sockRef.current.close(); };
    }, [openSocket]);

    // Invite link (?si_room=<id>): join once on load unless resuming a saved seat.
    const autoJoinRef = useRef(false);
    useEffect(() => {
        if (autoJoinRef.current) return;
        autoJoinRef.current = true;
        const id = siRoomIdFromLocation();
        if (!id) return;
        const saved = loadSiSeat();
        if (saved && saved.room_id && saved.seat_token) return;
        inviteRoomStatus(siRooms, id).then((st) => {
            if (st.join) { run(() => siRooms.join(id)); return; }
            clearRoomParam('si_room'); // a reload must not try this room again
            setMessage(st.message);
        });
    }, [run]);

    // Close an unjoined room on tab close (a running match uses the grace window).
    useEffect(() => {
        const onHide = () => {
            const g = grantRef.current;
            if (g && phaseRef.current === 'waiting') beaconLeaveSi(g.room_id, g.seat_token);
        };
        window.addEventListener('pagehide', onHide);
        return () => window.removeEventListener('pagehide', onHide);
    }, []);

    // Open-room list while idle.
    useEffect(() => {
        if (phase !== 'idle') return undefined;
        let stop = false;
        const load = () => siRooms.list().then((r) => { if (!stop) setWaitingRooms(r); }).catch(() => {});
        load();
        const id = setInterval(load, 4000);
        return () => { stop = true; clearInterval(id); };
    }, [phase]);

    // Render loop: interpolate SI_INTERP_DELAY_MS behind the newest snapshot.
    useEffect(() => {
        if (!grant) return undefined;
        let raf = 0;
        let frame = 0;
        const loop = () => {
            frame += 1;
            const canvas = canvasRef.current;
            const ctx = canvas && canvas.getContext ? canvas.getContext('2d') : null;
            const snap = sampleSiSnapshot(bufRef.current, performance.now() - SI_INTERP_DELAY_MS);
            if (ctx && snap) {
                drawSpaceInvaderScene(ctx, {
                    invaders: snap.invaders || [],
                    bullets: snap.bullets || [],
                    ships: sceneShips(snap, frame),
                    level: snap.level,
                });
            }
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, [grant]);

    // Keyboard → this ship only (← → / A D hold, Space fire, repeated while held).
    useEffect(() => {
        if (!grant) return undefined;
        const sendHeld = () => {
            const a = siHeldMove(keysRef.current);
            if (a !== lastSentRef.current && sockRef.current) {
                if (sockRef.current.sendAction(a)) lastSentRef.current = a;
            }
        };
        const stopFire = () => {
            if (fireTimerRef.current) clearInterval(fireTimerRef.current);
            fireTimerRef.current = null;
        };
        const down = (e) => {
            if (isEditableTarget(e.target)) return;
            if (e.key === ' ' || e.key.startsWith('Arrow')) e.preventDefault();
            if (e.repeat) return;
            if (e.key === ' ') {
                if (sockRef.current) sockRef.current.sendAction('fire');
                stopFire();
                fireTimerRef.current = setInterval(() => {
                    if (sockRef.current) sockRef.current.sendAction('fire');
                }, SI_FIRE_REPEAT_MS);
                return;
            }
            keysRef.current.add(e.key);
            sendHeld();
        };
        const up = (e) => {
            if (e.key === ' ') { stopFire(); return; }
            keysRef.current.delete(e.key);
            sendHeld();
        };
        const blur = () => { stopFire(); keysRef.current.clear(); sendHeld(); };
        window.addEventListener('keydown', down);
        window.addEventListener('keyup', up);
        window.addEventListener('blur', blur);
        // Re-send the held move after a reconnect (the server resets the seat to stay).
        const resend = setInterval(() => {
            if (conn === 'open' && lastSentRef.current !== 'stay' && sockRef.current) {
                sockRef.current.sendAction(lastSentRef.current);
            }
        }, 1000);
        return () => {
            window.removeEventListener('keydown', down);
            window.removeEventListener('keyup', up);
            window.removeEventListener('blur', blur);
            clearInterval(resend);
            stopFire();
        };
    }, [grant, conn]);

    const mySeat = grant?.seat;
    const other = mySeat === 'player_1' ? 'player_2' : 'player_1';
    const otherSeat = seats[other];
    const link = grant ? siInviteLink(grant.room_id) : '';

    let banner = '';
    if (phase === 'waiting') banner = 'Waiting for a partner — send them the invite link or room code.';
    else if (phase === 'ready') banner = 'Partner found — connecting…';
    else if (phase === 'paused') banner = 'Partner disconnected — waiting for them to come back…';
    else if (conn === 'reconnecting') banner = 'Connection lost — reconnecting…';

    let resultText = '';
    if (result) {
        resultText = `${END_TEXT[result.reason] || 'Game over'} · team score ${result.team_score ?? hud.team_score}`;
        if (result.level) resultText += ` · level ${result.level}`;
    }

    const myStats = hud.ships?.[mySeat];
    const otherStats = hud.ships?.[other];

    return (
        <ArcadeGameShell
            title={SI_DISPLAY_NAME}
            subtitle="Online Co-op — two ships on two computers, one fleet, shared lives"
            accentColor={ACCENT}
            headerContent={headerContent}
            boilerplate={GAME_BOILERPLATE.space_invader}
            session={session}
            growPlayfield
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%', gap: 1 }}>
                {!grant && (
                    <Box sx={PANEL_SX} data-testid="si-coop-lobby">
                        <Typography sx={{ color: COLOR.text, fontWeight: 700, mb: 1 }}>Play co-op online</Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                            <Button variant="contained" onClick={createRoom} disabled={busy} sx={{ bgcolor: ACCENT }}>
                                Create room
                            </Button>
                            <Button variant="outlined" onClick={quickMatch} disabled={busy}>Quick match</Button>
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            <TextField
                                size="small"
                                label="Room code"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && code.trim()) joinRoom(); }}
                                inputProps={{ 'aria-label': 'Room code' }}
                                sx={{ flex: 1 }}
                            />
                            <Button variant="outlined" onClick={() => joinRoom()} disabled={busy || !code.trim()}>Join</Button>
                        </Stack>
                        <Typography sx={{ color: COLOR.textMuted, fontSize: '0.8rem', mb: 0.5 }}>Open rooms</Typography>
                        {waitingRooms.length === 0 && (
                            <Typography sx={{ color: COLOR.textSubtle, fontSize: '0.8rem' }}>
                                None right now — create one and send the link to a friend.
                            </Typography>
                        )}
                        <Stack spacing={0.5}>
                            {waitingRooms.map((r) => (
                                <Stack key={r.room_id} direction="row" spacing={1} alignItems="center">
                                    <Typography sx={{ color: COLOR.text, fontSize: '0.85rem', flex: 1 }}>
                                        {r.seats?.player_1?.username || 'Guest'} · {r.room_id}
                                    </Typography>
                                    <Button size="small" onClick={() => joinRoom(r.room_id)} disabled={busy}>Join</Button>
                                </Stack>
                            ))}
                        </Stack>
                    </Box>
                )}
                {!grant && <CoopLeaderboard gameId="space_invader_coop" accentColor={ACCENT} />}

                {grant && (
                    <Box sx={{ ...PANEL_SX, py: 1.25 }} data-testid="si-coop-status">
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`You: ${SHIP_LABEL[mySeat] || mySeat}${myStats ? ` · ${myStats.kills} kills` : ''}`}
                                sx={{ color: SHIP_COLOR[mySeat] || COLOR.text, borderColor: SHIP_COLOR[mySeat] || ACCENT }}
                            />
                            <Chip
                                size="small"
                                variant="outlined"
                                sx={{ color: SHIP_COLOR[other] || COLOR.textMuted }}
                                label={otherSeat
                                    ? `${otherSeat.username || 'Guest'}${otherSeat.connected ? '' : ' (offline)'}${otherStats ? ` · ${otherStats.kills} kills` : ''}`
                                    : 'Open seat'}
                            />
                            <Chip size="small" data-testid="si-coop-score" label={`Team ${hud.team_score}`} sx={{ color: '#FFD700' }} variant="outlined" />
                            <Chip size="small" data-testid="si-coop-lives" label={`Lives ${hud.lives}`} sx={{ color: COLOR.text }} variant="outlined" />
                            <Chip size="small" data-testid="si-coop-level" label={formatSiLevel(hud.level, hud.max_level)} sx={{ color: COLOR.textMuted }} variant="outlined" />
                            <Box sx={{ flex: 1 }} />
                            <Button size="small" color="inherit" onClick={leave} sx={{ color: COLOR.textMuted }}>
                                {phase === 'finished' ? 'Back' : 'Leave'}
                            </Button>
                        </Stack>
                        {phase === 'waiting' && (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }} alignItems={{ sm: 'center' }}>
                                <Typography sx={{ color: COLOR.text, fontSize: '0.85rem' }}>
                                    Room code <b>{grant.room_id}</b>
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => { try { navigator.clipboard.writeText(link); setMessage('Invite link copied.'); } catch (_) { setMessage(link); } }}
                                >
                                    Copy invite link
                                </Button>
                            </Stack>
                        )}
                    </Box>
                )}

                {(banner || message || resultText) && (
                    <Typography role="status" sx={{ textAlign: 'center', color: resultText ? COLOR.text : COLOR.textMuted, fontWeight: resultText ? 700 : 400 }}>
                        {resultText || banner || message}
                    </Typography>
                )}

                {grant && (
                    <Box sx={{ ...SI_PLAYFIELD_SX, position: 'relative' }}>
                        <canvas
                            ref={canvasRef}
                            data-testid="si-coop-canvas"
                            width={SI_CANVAS_W}
                            height={SI_CANVAS_H}
                            role="img"
                            aria-label={`${SI_DISPLAY_NAME} co-op playfield`}
                            style={SI_CANVAS_STYLE}
                        />
                        <SiLevelBanner level={hud.level} maxLevel={hud.max_level} transition={hud.transition} won={hud.won} />
                    </Box>
                )}
                <Typography sx={{ color: COLOR.textSubtle, fontSize: '0.75rem', textAlign: 'center' }}>
                    ← → or A D move · hold Space to fire · you fly {SHIP_LABEL[mySeat] || 'your ship'} only · lives and score are shared
                </Typography>
            </Box>
        </ArcadeGameShell>
    );
}
